import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, MenuBurger, Search, Timer } from '@/components/ui/icons';
import { desktop } from '@/lib/desktop';
import { useDb, useMe, useStore } from '@/lib/store';
import { useUi } from '@/lib/ui';
import { stopTimer, runningTimer } from '@/lib/actions';
import { formatDuration } from '@/lib/format';
import { Tooltip } from '@/components/ui/Tooltip';
import { useEffect, useState } from 'react';

function RunningTimer() {
  const me = useMe();
  const entry = useDb((db) => runningTimer(db, me));
  const task = useDb((db) => (entry ? db.tasks[entry.taskId] : undefined));
  const [, tick] = useState(0);
  useEffect(() => {
    if (!entry) return;
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [entry]);
  if (!entry) return null;
  return (
    <Tooltip label={task ? `Tracking: ${task.name}` : 'Timer running'}>
      <button className="topbar-timer" onClick={() => stopTimer()}>
        <span className="topbar-timer-dot" />
        <Timer size={14} />
        <span>{formatDuration(Date.now() - entry.start, { seconds: true })}</span>
      </button>
    </Tooltip>
  );
}

/** Desktop app on Windows: the hidden title bar has no menu bar, so the app's menus sit in one row here. */
function AppMenuRow({ labels, show }: { labels: string[]; show: (x: number, y: number, index?: number) => void }) {
  return (
    <nav className="topbar-menus" aria-label="App menu">
      {labels.map((label, i) => (
        <button
          key={label}
          className="topbar-menu"
          onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            show(r.left, r.bottom + 2, i);
          }}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}

export function TopBar() {
  const navigate = useNavigate();
  const connection = useStore((s) => s.connection);
  const bridge = desktop();
  const showMenu = bridge?.platform === 'win32' ? bridge.showAppMenu : undefined;
  const [menuLabels, setMenuLabels] = useState<string[] | null>(null);
  useEffect(() => {
    if (showMenu) void bridge?.getAppMenuLabels?.().then(setMenuLabels);
  }, [bridge, showMenu]);

  return (
    <header className="topbar">
      <div className="topbar-left">
        {showMenu && menuLabels?.length ? <AppMenuRow labels={menuLabels} show={showMenu} /> : null}
        {/* desktop builds from before the menu row open the whole app menu from a ☰ button */}
        {showMenu && !bridge?.getAppMenuLabels && (
          <Tooltip label="Menu">
            <button
              className="icon-btn sm"
              aria-label="Menu"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                showMenu(r.left, r.bottom + 4);
              }}
            >
              <MenuBurger size={16} />
            </button>
          </Tooltip>
        )}
      </div>

      <div className="topbar-center">
        <Tooltip label="Back">
          <button className="icon-btn sm" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </button>
        </Tooltip>
        <Tooltip label="Forward">
          <button className="icon-btn sm" onClick={() => navigate(1)}>
            <ArrowRight size={16} />
          </button>
        </Tooltip>
        <div className="search-box" onClick={() => useUi.setState({ commandOpen: true })} role="button">
          <Search size={14} />
          <span className="search-box-text">Search</span>
          <span className="search-box-kbd">Ctrl K</span>
        </div>
      </div>

      <div className="topbar-right">
        {connection === 'offline' && <span className="offline-badge">Offline — changes will sync</span>}
        <RunningTimer />
      </div>
    </header>
  );
}
